import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Pagination } from './ui/pagination';
import { ListSkeleton, CardSkeleton } from './ui/skeleton';
import { usePagination } from '../hooks/usePagination';
import { useDebounce, useDebouncedSearch } from '../hooks/useDebounce';
import { useMobile } from '../hooks/useMobile';
import MobileSidebar from './MobileSidebar';
import NegotiationsPanel from './negotiations/NegotiationsPanel';
import DocumentsPanel from './documents/DocumentsPanel';
import { Building2, ChevronRight, Mail, Phone } from 'lucide-react';

// ─── Timeline Unificada ────────────────────────────────────────────────────────
const ACTIVITY_ICONS = {
  email: '📧', call: '📞', meeting: '📅', note: '📝',
  whatsapp: '💬', sms: '📱', task: '✅', stage_change: '➡️',
  interaction: '💬', default: '⚡',
};
const ACTIVITY_COLORS = {
  email: 'bg-blue-100 text-blue-700 border-blue-200',
  call: 'bg-green-100 text-green-700 border-green-200',
  meeting: 'bg-purple-100 text-purple-700 border-purple-200',
  note: 'bg-amber-100 text-amber-700 border-amber-200',
  whatsapp: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  sms: 'bg-teal-100 text-teal-700 border-teal-200',
  task: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  stage_change: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  interaction: 'bg-slate-100 text-slate-700 border-slate-200',
  default: 'bg-gray-100 text-gray-600 border-gray-200',
};

function LeadTimeline({ interactions = [], activities = [] }) {
  // Merge interactions and activities into a unified timeline
  const items = [
    ...interactions.map(i => ({
      id: `int-${i.id}`, type: i.type || 'interaction',
      title: i.subject || 'Interação', body: i.description || '',
      date: i.created_at, source: 'interaction',
    })),
    ...activities.map(a => ({
      id: `act-${a.id || a._id}`, type: a.type || 'default',
      title: a.title || a.subject || a.type || 'Atividade', body: a.description || a.notes || '',
      date: a.created_at || a.date, source: 'activity',
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  if (items.length === 0) return (
    <div className="text-center py-12 text-slate-400">
      <div className="text-4xl mb-3">📋</div>
      <p className="font-medium text-slate-600">Nenhuma atividade registrada</p>
      <p className="text-sm mt-1">As interações e atividades do lead aparecerão aqui</p>
    </div>
  );

  return (
    <div className="relative">
      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />
      <div className="space-y-4 pl-12">
        {items.map((item, idx) => {
          const icon = ACTIVITY_ICONS[item.type] || ACTIVITY_ICONS.default;
          const colorClass = ACTIVITY_COLORS[item.type] || ACTIVITY_COLORS.default;
          const dateStr = item.date ? new Date(item.date).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
          return (
            <div key={item.id} className="relative">
              <div className={`absolute -left-7 w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm ${colorClass}`}>
                {icon}
              </div>
              <div className="bg-white border border-slate-100 rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="font-medium text-slate-800 text-sm">{item.title}</p>
                    {item.body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{item.body}</p>}
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="text-xs text-slate-400">{dateStr}</span>
                    <div className={`text-xs px-1.5 py-0.5 rounded mt-0.5 ${colorClass} border`}>{item.type}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function Leads({ api }) {
  // Mobile detection
  const { isMobile, isTablet } = useMobile();
  
  // Basic state
  const [funnels, setFunnels] = useState([]);
  const [prospectLists, setProspectLists] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [leadActivities, setLeadActivities] = useState([]);
  const [detailTab, setDetailTab] = useState('interacoes');
  const [statusFilter, setStatusFilter] = useState('all');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingLead, setAnalyzingLead] = useState(null);
  const [leadFunnelStages, setLeadFunnelStages] = useState([]);
  const [addFunnelStages, setAddFunnelStages] = useState([]);
  const [lead360Data, setLead360Data] = useState(null);
  const [loading360, setLoading360] = useState(false);

  // Troca de empresa (mantém o histórico do lead na empresa antiga e
  // começa um lead novo, limpo, na empresa nova)
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferLead, setTransferLead] = useState(null);
  const [transferCompanyId, setTransferCompanyId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Search with debouncing
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Evita que o useEffect de filtros dispare um fetch duplicado na montagem,
  // já que o usePagination já busca os dados na primeira renderização.
  const skipFirstFilterRender = useRef(true);

  // Pagination with API integration
  const fetchLeadsWithParams = useCallback(async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 20,
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      ...(statusFilter !== 'all' && { status: statusFilter }),
      ...(showArchived && { include_archived: 'true' })
    });

    const response = await api.get(`/leads?${queryParams}`);
    return response.data;
  }, [api, debouncedSearchTerm, statusFilter, showArchived]);
  
  const {
    data: leads,
    loading,
    error,
    pagination,
    goToPage,
    changePageSize,
    refresh,
    isEmpty,
    hasData
  } = usePagination(fetchLeadsWithParams, { page: 1, limit: isMobile ? 10 : 20 });
  
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    company_id: '',
    source: 'website',
    notes: '',
    funnel_id: '',
    prospect_list_id: '',
    status: '',
    segment: ''
  });

  const [newInteraction, setNewInteraction] = useState({
    type: 'note',
    subject: '',
    description: ''
  });

  useEffect(() => {
    fetchFunnels();
    fetchProspectLists();
    fetchCompanies();
  }, []);

  // Refresh pagination when filters change
  useEffect(() => {
    if (skipFirstFilterRender.current) {
      skipFirstFilterRender.current = false;
      return;
    }
    refresh();
  }, [debouncedSearchTerm, statusFilter, showArchived, refresh]);

  const fetchFunnels = async () => {
    try {
      const response = await api.get('/funnels');
      setFunnels(response.data);
    } catch (error) {
      console.error('Erro ao carregar funis:', error);
    }
  };

  const fetchProspectLists = async () => {
    try {
      const response = await api.get('/prospecting/lists');
      setProspectLists(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar listas de prospecção:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  };
  
  // Fetch stages for a specific funnel (used in lead editing)
  const fetchFunnelStages = async (funnelId) => {
    if (!funnelId) {
      setLeadFunnelStages([]);
      return;
    }
    
    try {
      // First try to get from funnel directly
      const funnel = funnels.find(f => f.id === funnelId);
      if (funnel && funnel.stages && funnel.stages.length > 0) {
        // Convert stages (strings) to objects with id and name
        const stagesAsObjects = funnel.stages.map((stageName, index) => ({
          id: `stage-${index}`,
          name: typeof stageName === 'string' ? stageName : stageName
        }));
        setLeadFunnelStages(stagesAsObjects);
        return;
      }
      
      // If funnel doesn't have stages, try to get from pipeline
      try {
        const response = await api.get(`/pipelines/by-funnel/${funnelId}`);
        if (response.data && response.data.stages) {
          setLeadFunnelStages(response.data.stages);
        } else {
          setLeadFunnelStages([]);
        }
      } catch (pipelineError) {
        // Pipeline might not exist, use fallback options
        setLeadFunnelStages([]);
      }
    } catch (error) {
      console.error('Erro ao carregar stages do funil:', error);
      setLeadFunnelStages([]);
    }
  };

  const handleAddLead = async () => {
    // Validate required fields
    if (!newLead.name || newLead.name.trim() === '') {
      toast.error('Por favor, insira um nome para o lead');
      return;
    }

    if (!newLead.email || newLead.email.trim() === '') {
      toast.error('Por favor, insira um email para o lead');
      return;
    }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newLead.email.trim())) {
      toast.error('Por favor, insira um email válido para o lead');
      return;
    }

    if (!newLead.name || newLead.name.trim() === '') {
      toast.error('Por favor, insira um nome para o lead');
      return;
    }


    if (companies.length > 0 && !newLead.company_id) {
      toast.error('Por favor, selecione uma empresa para o lead');
      return;
    }

    if (!newLead.status) {
      toast.error('Por favor, selecione um status para o lead');
      return;
    }
    
    try {
      // Create the lead first
      await api.post('/leads', newLead);
      
      // If a prospect list is selected, add the lead data to that list
      if (newLead.prospect_list_id && newLead.prospect_list_id !== 'none') {
        try {
          await api.post(`/prospecting/lists/${newLead.prospect_list_id}/prospects`, {
            name: newLead.name,
            email: newLead.email,
            phone: newLead.phone,
            company: newLead.company,
            notes: newLead.notes
          });
          toast.success('Lead adicionado com sucesso e enviado para a lista de prospecção!');
        } catch (listError) {
          console.error('Erro ao adicionar lead à lista de prospecção:', listError);
          toast.success('Lead adicionado com sucesso, mas houve um erro ao enviá-lo para a lista de prospecção');
        }
      } else {
        toast.success('Lead adicionado com sucesso!');
      }
      
      setShowAddModal(false);
      setNewLead({
        name: '',
        email: '',
        phone: '',
        company: '',
        company_id: '',
        source: 'website',
        notes: '',
        funnel_id: '',
        prospect_list_id: 'none',
        status: ''
      });
      refresh(); // Use refresh instead of fetchLeads
    } catch (error) {
      toast.error('Erro ao adicionar lead');
      console.error(error);
    }
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;
    
    // Validate required fields
    if (!selectedLead.status) {
      toast.error('Por favor, selecione um status para o lead');
      return;
    }
    
    if (!selectedLead.name || selectedLead.name.trim() === '') {
      toast.error('Por favor, insira um nome para o lead');
      return;
    }
    
    try {
      // console.log('🔄 Atualizando lead:', selectedLead);
      const response = await api.put(`/leads/${selectedLead.id}`, selectedLead);
      // console.log('✅ Resposta da atualização:', response);
      
      // If a prospect list is selected, add the lead data to that list
      if (selectedLead.prospect_list_id && selectedLead.prospect_list_id !== 'none') {
        try {
          await api.post(`/prospecting/lists/${selectedLead.prospect_list_id}/prospects`, {
            name: selectedLead.name,
            email: selectedLead.email,
            phone: selectedLead.phone,
            company: selectedLead.company,
            notes: selectedLead.notes
          });
          toast.success('Lead atualizado com sucesso e enviado para a lista de prospecção!');
        } catch (listError) {
          console.error('Erro ao adicionar lead à lista de prospecção:', listError);
          toast.success('Lead atualizado com sucesso, mas houve um erro ao enviá-lo para a lista de prospecção');
        }
      } else {
        toast.success('Lead atualizado com sucesso!');
      }
      
      setShowEditModal(false);
      setSelectedLead(null);
      refresh(); // Use refresh instead of fetchLeads
    } catch (error) {
      // console.error('❌ Erro completo ao atualizar lead:', error);
      // console.error('📋 Dados do erro:', error.response?.data);
      // console.error('🔢 Status do erro:', error.response?.status);
      // console.error('🔍 Lead sendo enviado:', selectedLead);
      
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Erro desconhecido';
      toast.error(`Erro ao atualizar lead: ${errorMessage}`);
    }
  };

  const handleDeleteLead = async (leadId) => {
    // Debug: verificar o ID do lead
    // console.log('🔍 Debug - ID do lead a ser deletado:', leadId);
    // console.log('🔍 Debug - Tipo do ID:', typeof leadId);
    
    if (!leadId) {
      toast.error('ID do lead inválido');
      return;
    }
    
    if (!window.confirm('Tem certeza que deseja excluir este lead?')) return;
    
    try {
      // console.log('🗑️ Tentando deletar lead:', leadId);
      const response = await api.delete(`/leads/${leadId}`);
      // console.log('✅ Resposta do delete:', response);
      toast.success('Lead excluído com sucesso!');
      refresh(); // Use refresh instead of fetchLeads
    } catch (error) {
      // console.error('❌ Erro completo ao deletar lead:', error);
      // console.error('📋 Dados do erro:', error.response?.data);
      // console.error('🔢 Status do erro:', error.response?.status);
      
      if (error.response?.status === 403) {
        toast.error('Você não tem permissão para excluir leads');
      } else if (error.response?.status === 404) {
        toast.error('Lead não encontrado no sistema. Verifique se ainda existe.');
      } else {
        toast.error(`Erro ao excluir lead: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const handleViewDetails = async (lead) => {
    // Close any open modals first
    setShowEditModal(false);
    setShowAddModal(false);
    
    // Create a deep copy to avoid reference issues
    setSelectedLead({ ...lead });
    setShowDetailsModal(true);
    
    // Fetch stages for this lead's funnel
    if (lead.funnel_id) {
      fetchFunnelStages(lead.funnel_id);
    } else {
      setLeadFunnelStages([]);
    }
    
    setDetailTab('interacoes');
    setLeadActivities([]);
    try {
      // Fetch interactions and activities in parallel
      const [interactionsResponse, activitiesResponse] = await Promise.allSettled([
        api.get(`/leads/${lead.id}/interactions`),
        api.get(`/leads/${lead.id}/activities`),
      ]);
      if (interactionsResponse.status === 'fulfilled' && interactionsResponse.value?.data) {
        setInteractions(interactionsResponse.value.data);
      }
      if (activitiesResponse.status === 'fulfilled' && activitiesResponse.value?.data) {
        setLeadActivities(activitiesResponse.value.data);
      }
    } catch (error) {
      //console.log('No interactions found for this lead');
    }
  };

  const fetch360Data = async (lead) => {
    setLoading360(true);
    setLead360Data(null);
    try {
      const [dealsRes] = await Promise.allSettled([
        api.get(`/deals?contact_name=${encodeURIComponent(lead.name || '')}&limit=10`),
      ]);
      setLead360Data({
        deals: dealsRes.status === 'fulfilled' ? (Array.isArray(dealsRes.value.data) ? dealsRes.value.data : dealsRes.value.data?.deals || []) : [],
      });
    } catch {
      setLead360Data({ deals: [] });
    } finally {
      setLoading360(false);
    }
  };

  const handleEditLead = (lead) => {
    // Close any open modals first
    setShowDetailsModal(false);
    setShowAddModal(false);
    
    // Create a deep copy to avoid reference issues
    setSelectedLead({ ...lead });
    setShowEditModal(true);
    
    // Fetch stages for this lead's funnel
    if (lead.funnel_id) {
      fetchFunnelStages(lead.funnel_id);
    } else {
      setLeadFunnelStages([]);
    }
  };

  const openTransferModal = (lead) => {
    setTransferLead(lead);
    setTransferCompanyId('');
    setTransferReason('');
    setShowTransferModal(true);
  };

  const handleTransferCompany = async () => {
    if (!transferLead || !transferCompanyId) {
      toast.error('Selecione a empresa de destino');
      return;
    }

    try {
      setTransferring(true);
      await api.post(`/leads/${transferLead.id}/transfer-company`, {
        new_company_id: transferCompanyId,
        reason: transferReason || undefined
      });
      toast.success('Lead transferido! O histórico continua na empresa antiga e um lead novo foi criado na empresa nova.');
      setShowTransferModal(false);
      setTransferLead(null);
      refresh();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erro desconhecido';
      toast.error(`Erro ao trocar empresa do lead: ${errorMessage}`);
    } finally {
      setTransferring(false);
    }
  };

  const handleAddInteraction = async () => {
    if (!selectedLead) return;
    
    try {
      await api.post('/interactions', {
        ...newInteraction,
        lead_id: selectedLead.id
      });
      toast.success('Interação adicionada com sucesso!');
      setNewInteraction({
        type: 'note',
        subject: '',
        description: ''
      });
      
      // Reload interactions
      const response = await api.get(`/interactions/${selectedLead.id}`);
      setInteractions(response.data);
    } catch (error) {
      toast.error('Erro ao adicionar interação');
      console.error(error);
    }
  };

  const analyzeLeadWithAI = async (leadId) => {
    try {
      setAnalyzingLead(leadId);
      const response = await api.post(`/leads/${leadId}/ai-analysis`);
      setAiAnalysis(response.data);
      setShowAIModal(true);
      
      toast.success('Análise de IA concluída!');
      
      // Refresh the list to get updated data
      refresh();
    } catch (error) {
      toast.error('Erro na análise de IA');
      console.error(error);
    } finally {
      setAnalyzingLead(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-gradient-to-r from-green-500 to-emerald-600 text-white';
    if (score >= 60) return 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white';
    if (score >= 40) return 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white';
    return 'bg-gradient-to-r from-red-500 to-rose-600 text-white';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Alta Qualidade';
    if (score >= 60) return 'Boa Qualidade';
    if (score >= 40) return 'Qualidade Média';
    return 'Baixa Qualidade';
  };

  const getSentimentEmoji = (sentiment) => {
    switch (sentiment) {
      case 'positive': return '😊';
      case 'negative': return '😟';
      default: return '😐';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-amber-100 text-amber-800 border-amber-200',
      'contacted': 'bg-blue-100 text-blue-800 border-blue-200',
      'qualified': 'bg-purple-100 text-purple-800 border-purple-200',
      'proposal': 'bg-cyan-100 text-cyan-800 border-cyan-200',
      'negotiation': 'bg-orange-100 text-orange-800 border-orange-200',
      'won': 'bg-green-100 text-green-800 border-green-200',
      'lost': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'Novo',
      'contacted': 'Contatado',
      'qualified': 'Qualificado',
      'proposal': 'Proposta',
      'negotiation': 'Negociação',
      'won': 'Ganho',
      'lost': 'Perdido'
    };
    return labels[status] || status;
  };

  // Filter logic is now handled by the API through pagination hook
  
  // Loading state with skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-slate-200 rounded w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-slate-200 rounded w-48 animate-pulse"></div>
          </div>
          <div className="h-10 bg-slate-200 rounded w-32 animate-pulse"></div>
        </div>
        
        <ListSkeleton items={isMobile ? 5 : 10} />
      </div>
    );
  }

  return (
    <div className="leads-journey space-y-6 animate-fade-in" data-testid="leads-page">
      {/* Header */}
      <div className={`flex items-center justify-between ${isMobile ? 'flex-col space-y-4' : 'flex-row'}`}>
        <div className={isMobile ? 'text-center' : ''}>
          <h1 className={`font-bold text-slate-900 ${isMobile ? 'text-2xl' : 'text-3xl'}`}>Gestão de Leads</h1>
          <p className="text-slate-600 mt-1">Gerencie seus leads e oportunidades</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-lead-button">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Adicionar Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Adicionar Novo Lead</DialogTitle>
              <DialogDescription>Cadastre um novo lead no sistema</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  data-testid="lead-name-input"
                  value={newLead.name}
                  onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                  placeholder="Nome completo"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="lead-email-input"
                  value={newLead.email}
                  onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                  placeholder="email@exemplo.com"
                  className={!newLead.email ? 'border-red-400' : ''}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    data-testid="lead-phone-input"
                    value={newLead.phone}
                    onChange={(e) => setNewLead({...newLead, phone: e.target.value.replace(/\D/g, '').slice(0, 11).replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')})}
                    placeholder="(11) 99999-9999"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company">Empresa <span className="text-red-500">*</span></Label>
                  {companies.length > 0 ? (
                    <Select
                      value={newLead.company_id || ''}
                      onValueChange={(v) => {
                        const chosen = companies.find(c => c.id === v);
                        setNewLead({ ...newLead, company_id: v, company: chosen ? chosen.name : '' });
                      }}
                    >
                      <SelectTrigger id="company" data-testid="lead-company-input" className={!newLead.company_id ? 'border-red-400' : ''}>
                        <SelectValue placeholder="Selecione a empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                      Nenhuma empresa cadastrada. <button type="button" className="underline font-medium" onClick={() => window.dispatchEvent(new CustomEvent('nexosynx:navigate', { detail: 'companies' }))}>Cadastrar empresa</button> antes de criar um lead.
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="source">Origem</Label>
                <Select 
                  value={newLead.source} 
                  onValueChange={(value) => setNewLead({...newLead, source: value})}
                >
                  <SelectTrigger data-testid="lead-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="social">Redes Sociais</SelectItem>
                    <SelectItem value="email">Email Marketing</SelectItem>
                    <SelectItem value="referral">Indicação</SelectItem>
                    <SelectItem value="ads">Anúncios</SelectItem>
                    <SelectItem value="other">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="funnel">Funil *</Label>
                <Select 
                  value={newLead.funnel_id} 
                  onValueChange={(value) => {
                    setNewLead({...newLead, funnel_id: value});
                    // Fetch stages for the selected funnel
                    if (value) {
                      const funnel = funnels.find(f => f.id === value);
                      if (funnel && funnel.stages && funnel.stages.length > 0) {
                        const stagesAsObjects = funnel.stages.map((stageName, index) => ({
                          id: `stage-${index}`,
                          name: typeof stageName === 'string' ? stageName : stageName
                        }));
                        setAddFunnelStages(stagesAsObjects);
                      } else {
                        setAddFunnelStages([]);
                      }
                    } else {
                      setAddFunnelStages([]);
                    }
                  }}
                >
                  <SelectTrigger data-testid="lead-funnel-select" className={!newLead.funnel_id ? 'border-red-400' : ''}>
                  <SelectValue placeholder="Selecione um funil" />
                </SelectTrigger>
                  <SelectContent>
                    {funnels.map((funnel) => (
                      <SelectItem key={funnel.id} value={funnel.id}>
                        {funnel.name} ({funnel.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status">Status <span className="text-red-500">*</span></Label>
                <Select 
                  value={newLead.status || ''} 
                  onValueChange={(value) => setNewLead({...newLead, status: value})}
                >
                  <SelectTrigger data-testid="lead-status-select" className={!newLead.status ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    {addFunnelStages.length > 0 ? (
                      addFunnelStages.map((stage) => (
                        <SelectItem key={stage.id || stage.name} value={stage.name}>
                          {stage.name}
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="new">Novo</SelectItem>
                        <SelectItem value="contacted">Contatado</SelectItem>
                        <SelectItem value="qualified">Qualificado</SelectItem>
                        <SelectItem value="proposal">Proposta</SelectItem>
                        <SelectItem value="negotiation">Negociação</SelectItem>
                        <SelectItem value="won">Ganho</SelectItem>
                        <SelectItem value="lost">Perdido</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="prospect-list">Lista de Prospecção</Label>
                <Select 
                  value={newLead.prospect_list_id} 
                  onValueChange={(value) => setNewLead({...newLead, prospect_list_id: value})}
                >
                  <SelectTrigger data-testid="lead-prospect-list-select">
                    <SelectValue placeholder="Selecione uma lista (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma lista</SelectItem>
                    {prospectLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name} ({list.prospects_count || 0} prospects)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="segment">Segmento</Label>
                <Select
                  value={newLead.segment || '__none__'}
                  onValueChange={(v) => setNewLead({...newLead, segment: v === '__none__' ? '' : v})}
                >
                  <SelectTrigger id="segment">
                    <SelectValue placeholder="Selecione um segmento (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum</SelectItem>
                    <SelectItem value="Tecnologia">Tecnologia</SelectItem>
                    <SelectItem value="Saúde">Saúde</SelectItem>
                    <SelectItem value="Educação">Educação</SelectItem>
                    <SelectItem value="Varejo">Varejo</SelectItem>
                    <SelectItem value="Indústria">Indústria</SelectItem>
                    <SelectItem value="Logística">Logística</SelectItem>
                    <SelectItem value="Financeiro">Financeiro</SelectItem>
                    <SelectItem value="Imobiliário">Imobiliário</SelectItem>
                    <SelectItem value="Agronegócio">Agronegócio</SelectItem>
                    <SelectItem value="Serviços">Serviços</SelectItem>
                    <SelectItem value="Outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Observações</Label>
                <Textarea
                  id="notes"
                  data-testid="lead-notes-input"
                  value={newLead.notes}
                  onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                  placeholder="Observações sobre o lead..."
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddLead} className="btn-primary" data-testid="save-lead-button">
                Salvar Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="glass border-white/20">
        <CardContent className="p-4 md:p-6">
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            <div className="space-y-2">
              <Label htmlFor="search">Buscar</Label>
              <Input
                id="search"
                data-testid="search-leads-input"
                placeholder={isMobile ? "Buscar..." : "Buscar por nome, email ou empresa..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white/50"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status-filter">Filtrar por Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-white/50" data-testid="status-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Status</SelectItem>
                  <SelectItem value="new">Novos</SelectItem>
                  <SelectItem value="contacted">Contatados</SelectItem>
                  <SelectItem value="qualified">Qualificados</SelectItem>
                  <SelectItem value="proposal">Proposta</SelectItem>
                  <SelectItem value="negotiation">Negociação</SelectItem>
                  <SelectItem value="won">Ganhos</SelectItem>
                  <SelectItem value="lost">Perdidos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="show-archived"
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="show-archived" className="cursor-pointer text-sm font-normal">
                Mostrar leads arquivados (transferidos de empresa)
              </Label>
            </div>

            {/* Quick stats for mobile */}
            {isMobile && hasData && (
              <div className="flex justify-between items-center pt-2 text-sm text-slate-600">
                <span>{pagination.total_items} leads</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={refresh}
                  disabled={loading}
                >
                  🔄 Atualizar
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Leads List */}
      <div className="space-y-4" data-testid="leads-list">
        {isEmpty ? (
          <Card className="glass border-white/20">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhum lead encontrado</h3>
              <p className="text-slate-500">Adicione seu primeiro lead ou ajuste os filtros</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Responsive grid for mobile */}
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="hidden items-center gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500 md:grid md:grid-cols-[minmax(240px,1.5fr)_minmax(180px,1fr)_140px_120px_140px]"><span>Lead</span><span>Contato e empresa</span><span>Etapa</span><span>Qualificação</span><span className="text-right">Ações</span></div>
              <div className="divide-y divide-slate-100">
              {leads.map((lead, index) => {
                // Debug: log lead structure
                if (index === 0) {
                  // console.log('🔍 Debug - Estrutura do primeiro lead:', lead);
                  // console.log('🔍 Debug - ID disponível:', lead.id);
                  // console.log('🔍 Debug - _id disponível:', lead._id);
                  // console.log('🔍 Debug - Todas as chaves:', Object.keys(lead));
                }
                
                return (
            <Card key={lead.id} className={`rounded-none border-0 shadow-none transition-colors hover:bg-slate-50 ${lead.archived ? 'opacity-60' : ''}`} data-testid={`lead-card-${lead.id}`}>
              <CardContent className="p-4 md:px-5 md:py-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{lead.name}</h3>
                      <Badge className={getStatusColor(lead.status)} data-testid={`lead-status-${lead.id}`}>
                        {getStatusLabel(lead.status)}
                      </Badge>
                      {lead.archived && (
                        <Badge className="bg-gray-200 text-gray-600 border-gray-300" title={lead.archived_reason || ''}>
                          📦 Arquivado
                        </Badge>
                      )}
                      {lead.previous_lead_id && (
                        <Badge className="bg-blue-50 text-blue-600 border-blue-200">
                          🔁 Veio de outra empresa
                        </Badge>
                      )}
                    </div>

                    <div className="grid gap-1 text-xs text-slate-600 sm:grid-cols-2 md:block">
                      <p className="flex items-center gap-1.5 truncate"><Mail size={13} className="shrink-0 text-slate-400" />{lead.email || 'Email não informado'}</p>
                      {lead.phone && <p className="flex items-center gap-1.5"><Phone size={13} className="shrink-0 text-slate-400" />{lead.phone}</p>}
                      {lead.company && <p className="flex items-center gap-1.5 truncate"><Building2 size={13} className="shrink-0 text-slate-400" />{lead.company}</p>}
                      <p className="text-[11px] text-slate-400">Origem: {lead.source || 'não informada'}</p>
                    </div>

                    {/* Score de IA */}
                    {lead.score > 0 && (
                      <div className="flex items-center space-x-2 mt-2">
                        <Badge className={`text-xs ${getScoreColor(lead.score)}`}>
                          🤖 Score: {lead.score}/100
                        </Badge>
                        <span className="text-xs text-slate-500">{getScoreLabel(lead.score)}</span>
                      </div>
                    )}
                    
                    {lead.notes && (
                      <p className="text-sm text-slate-500 mt-2 italic">{lead.notes}</p>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => analyzeLeadWithAI(lead.id)}
                      disabled={analyzingLead === lead.id}
                      className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200 hover:from-purple-100 hover:to-pink-100"
                      data-testid={`analyze-lead-${lead.id}`}
                      title="Analisar com IA"
                    >
                      {analyzingLead === lead.id ? (
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"></div>
                      ) : (
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      )}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewDetails(lead)}
                      className="bg-white/60"
                      data-testid={`view-lead-${lead.id}`}
                      title="Abrir workspace do lead"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    
                    {!lead.archived && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openTransferModal(lead)}
                        className="bg-white/60"
                        data-testid={`transfer-lead-${lead.id}`}
                        title="Trocar empresa (mantém o histórico na empresa antiga)"
                      >
                        <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditLead(lead)}
                      className="bg-white/60"
                      data-testid={`edit-lead-${lead.id}`}
                      title="Editar lead"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteLead(lead.id)}
                      data-testid={`delete-lead-${lead.id}`}
                      title="Excluir lead"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
              </div>
            </div>
            
            {/* Pagination */}
            {hasData && (
              <div className="mt-6">
                <Pagination
                  pagination={pagination}
                  onPageChange={goToPage}
                  onPageSizeChange={changePageSize}
                  loading={loading}
                  className="justify-center"
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => {
        setShowEditModal(open);
        if (!open) setSelectedLead(null); // Clear selected lead when closing
      }}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Lead</DialogTitle>
            <DialogDescription>Atualize as informações do lead</DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={selectedLead.email || ''}
                  onChange={(e) => setSelectedLead({...selectedLead, email: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nome</Label>
                <Input
                  id="edit-name"
                  value={selectedLead.name || ''}
                  onChange={(e) => setSelectedLead({...selectedLead, name: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-status">
                  Status <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={selectedLead.status || ''} 
                  onValueChange={(value) => setSelectedLead({...selectedLead, status: value})}
                >
                  <SelectTrigger className={!selectedLead.status ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadFunnelStages.length > 0 ? (
                      leadFunnelStages.map((stage) => (
                        <SelectItem key={stage.id || stage.name} value={stage.name}>
                          {stage.name}
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="new">Novo</SelectItem>
                        <SelectItem value="contacted">Contatado</SelectItem>
                        <SelectItem value="qualified">Qualificado</SelectItem>
                        <SelectItem value="proposal">Proposta</SelectItem>
                        <SelectItem value="negotiation">Negociação</SelectItem>
                        <SelectItem value="won">Ganho</SelectItem>
                        <SelectItem value="lost">Perdido</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Telefone</Label>
                <Input
                  id="edit-phone"
                  value={selectedLead.phone || ''}
                  onChange={(e) => setSelectedLead({...selectedLead, phone: e.target.value.replace(/\D/g, '').slice(0, 11).replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')})}                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-company">Empresa</Label>
                <Input
                  id="edit-company"
                  value={selectedLead.company || ''}
                  onChange={(e) => setSelectedLead({...selectedLead, company: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-funnel">Funil</Label>
                <Select 
                  value={selectedLead.funnel_id || ''} 
                  onValueChange={(value) => {
                    setSelectedLead({...selectedLead, funnel_id: value});
                    // Update stages when funnel changes
                    if (value) {
                      fetchFunnelStages(value);
                    } else {
                      setLeadFunnelStages([]);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um funil" />
                  </SelectTrigger>
                  <SelectContent>
                    {funnels.map((funnel) => (
                      <SelectItem key={funnel.id} value={funnel.id}>
                        {funnel.name} ({funnel.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-segment">Segmento</Label>
                <Select
                  value={selectedLead.segment || '__none__'}
                  onValueChange={(v) => setSelectedLead({...selectedLead, segment: v === '__none__' ? '' : v})}
                >
                  <SelectTrigger id="edit-segment">
                    <SelectValue placeholder="Selecione um segmento (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum</SelectItem>
                    <SelectItem value="Tecnologia">Tecnologia</SelectItem>
                    <SelectItem value="Saúde">Saúde</SelectItem>
                    <SelectItem value="Educação">Educação</SelectItem>
                    <SelectItem value="Varejo">Varejo</SelectItem>
                    <SelectItem value="Indústria">Indústria</SelectItem>
                    <SelectItem value="Logística">Logística</SelectItem>
                    <SelectItem value="Financeiro">Financeiro</SelectItem>
                    <SelectItem value="Imobiliário">Imobiliário</SelectItem>
                    <SelectItem value="Agronegócio">Agronegócio</SelectItem>
                    <SelectItem value="Serviços">Serviços</SelectItem>
                    <SelectItem value="Outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-prospect-list">Lista de Prospecção</Label>
                <Select 
                  value={selectedLead.prospect_list_id || ''} 
                  onValueChange={(value) => setSelectedLead({...selectedLead, prospect_list_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma lista (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma lista</SelectItem>
                    {prospectLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name} ({list.prospects_count || 0} prospects)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateLead} className="btn-primary">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Company Modal */}
      <Dialog open={showTransferModal} onOpenChange={(open) => {
        setShowTransferModal(open);
        if (!open) setTransferLead(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Trocar Empresa do Lead</DialogTitle>
            <DialogDescription>
              O lead atual será arquivado junto com todo o histórico dele (interações, atividades, negócios) na empresa
              atual. Um lead novo, limpo, será criado na empresa de destino.
            </DialogDescription>
          </DialogHeader>

          {transferLead && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                ⚠️ <strong>{transferLead.name}</strong> está atualmente em <strong>{transferLead.company || 'nenhuma empresa'}</strong>.
                O histórico não será movido — ele fica preservado na empresa atual.
              </div>

              <div className="space-y-2">
                <Label htmlFor="transfer-company">Nova Empresa <span className="text-red-500">*</span></Label>
                <Select value={transferCompanyId} onValueChange={setTransferCompanyId}>
                  <SelectTrigger id="transfer-company">
                    <SelectValue placeholder="Selecione a empresa de destino" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.filter(c => c.id !== transferLead.company_id).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="transfer-reason">Motivo (opcional)</Label>
                <Textarea
                  id="transfer-reason"
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  placeholder="Ex: cliente migrou de conta..."
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleTransferCompany} className="btn-primary" disabled={transferring || !transferCompanyId}>
              {transferring ? 'Transferindo...' : 'Confirmar Troca'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead detail screen */}
      {showDetailsModal && selectedLead && (
        <div className="lead-detail-screen fixed inset-0 z-40 overflow-y-auto bg-slate-50">
          <div className="mx-auto min-h-screen max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
            <div className="mb-5 flex items-center justify-between">
              <Button variant="ghost" onClick={() => { setShowDetailsModal(false); setSelectedLead(null); setLeadActivities([]); }}>
                ← <span className="hidden sm:inline">Voltar para </span>Leads
              </Button>
              <div className="flex items-center gap-2">
                <span className="hidden text-xs text-slate-400 md:inline">Leads / {selectedLead.name}</span>
                <Button variant="outline" onClick={() => handleEditLead(selectedLead)}>Editar lead</Button>
              </div>
            </div>
            <div className="mb-6 rounded-2xl bg-slate-950 px-6 py-6 text-white shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-300">Workspace do Lead</p>
              <h1 className="mt-1 text-3xl font-bold">{selectedLead.name}</h1>
              <p className="mt-1 text-slate-300">Contexto comercial, relacionamento e negociações em um só lugar.</p>
            </div>
            <div className="space-y-5">
              {selectedLead.archived && (
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-sm text-gray-700">
                  📦 Este lead foi arquivado{selectedLead.archived_reason ? `: ${selectedLead.archived_reason}` : ''}
                  {selectedLead.archived_at && ` em ${new Date(selectedLead.archived_at).toLocaleDateString('pt-BR')}`}.
                  O histórico abaixo continua pertencendo à empresa em que ele estava na época.
                </div>
              )}
              {selectedLead.previous_lead_id && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  🔁 Este lead nasceu de uma troca de empresa. O histórico anterior (interações, negócios) ficou
                  registrado no lead original, na empresa antiga.
                </div>
              )}

              {/* Lead Info */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Contato principal</p><h4 className="mt-1 text-xl font-bold text-slate-950">{selectedLead.name}</h4><p className="mt-1 text-sm text-slate-500">{selectedLead.company || 'Empresa não informada'}</p></div>
                  <div className="flex items-center gap-2"><Badge className={getStatusColor(selectedLead.status)}>{getStatusLabel(selectedLead.status)}</Badge>{selectedLead.score > 0 && <Badge className="bg-violet-50 text-violet-700">Score {selectedLead.score}/100</Badge>}</div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <span className="block text-xs text-slate-400">Email</span><span className="font-medium text-slate-700">{selectedLead.email || 'Não informado'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Telefone</span><span className="font-medium text-slate-700">{selectedLead.phone || 'Não informado'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Origem</span><span className="font-medium text-slate-700">{selectedLead.source || 'Não informada'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400">Etapa</span><span className="font-medium text-slate-700">{getStatusLabel(selectedLead.status)}</span>
                  </div>
                </div>
                {selectedLead.notes && (
                  <div className="mt-3">
                    <strong>Observações:</strong>
                    <p className="text-slate-600 mt-1">{selectedLead.notes}</p>
                  </div>
                )}
              </div>

              {/* Tabs: Interações | Timeline | 360° | Negociações | Documentos */}
              <div className="flex gap-1 overflow-x-auto rounded-xl bg-gray-100 p-0.5">
                {[{ id: 'interacoes', label: '💬 Interações' }, { id: 'timeline', label: '📋 Timeline' }, { id: '360', label: '🔍 360°' }, { id: 'negociacoes', label: '🤝 Negociações' }, { id: 'documentos', label: '📁 Documentos' }].map(t => (
                  <button key={t.id} onClick={() => { setDetailTab(t.id); if (t.id === '360' && !lead360Data && !loading360) fetch360Data(selectedLead); }}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${detailTab === t.id ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500'}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {detailTab === 'timeline' && (
                <LeadTimeline interactions={interactions} activities={leadActivities} />
              )}

              {detailTab === '360' && (
                <div className="space-y-4">
                  {loading360 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">Carregando visão 360°...</div>
                  ) : (
                    <>
                      {/* Score + stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center">
                          <div className="text-2xl font-bold text-blue-700">{selectedLead.score || 0}</div>
                          <div className="text-xs text-blue-600 mt-1">Lead Score</div>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center">
                          <div className="text-2xl font-bold text-purple-700">{interactions.length}</div>
                          <div className="text-xs text-purple-600 mt-1">Interações</div>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center">
                          <div className="text-2xl font-bold text-green-700">{(lead360Data?.deals || []).length}</div>
                          <div className="text-xs text-green-600 mt-1">Negócios</div>
                        </div>
                      </div>

                      {/* Interaction breakdown */}
                      <div className="bg-slate-50 rounded-xl p-4">
                        <h4 className="font-semibold text-slate-700 text-sm mb-3">Histórico de Contatos</h4>
                        <div className="grid grid-cols-4 gap-2">
                          {[['call','📞','Ligações'],['email','📧','Emails'],['meeting','🤝','Reuniões'],['note','📝','Notas']].map(([type, icon, label]) => (
                            <div key={type} className="text-center bg-white rounded-lg p-3 border">
                              <div className="text-lg">{icon}</div>
                              <div className="text-xl font-bold text-slate-700">{interactions.filter(i => i.type === type).length}</div>
                              <div className="text-xs text-slate-500">{label}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Funnel position */}
                      <div className="bg-slate-50 rounded-xl p-4">
                        <h4 className="font-semibold text-slate-700 text-sm mb-3">📍 Posição no Funil</h4>
                        {leadFunnelStages.length > 0 ? (
                          <div className="flex items-center gap-1 flex-wrap">
                            {leadFunnelStages.map((stage, idx) => {
                              const isActive = stage.name === selectedLead.status || stage.id === selectedLead.status;
                              return (
                                <div key={stage.id} className="flex items-center gap-1">
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${isActive ? 'bg-blue-600 text-white shadow' : 'bg-gray-200 text-gray-500'}`}>{stage.name}</span>
                                  {idx < leadFunnelStages.length - 1 && <span className="text-gray-300 text-xs">›</span>}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <Badge className={getStatusColor(selectedLead.status)}>{getStatusLabel(selectedLead.status)}</Badge>
                        )}
                      </div>

                      {/* Linked deals */}
                      <div className="bg-slate-50 rounded-xl p-4">
                        <h4 className="font-semibold text-slate-700 text-sm mb-3">💼 Negócios Vinculados</h4>
                        {(lead360Data?.deals || []).length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-2">Nenhum negócio encontrado para este lead</p>
                        ) : (
                          <div className="space-y-2">
                            {(lead360Data?.deals || []).slice(0, 5).map(deal => (
                              <div key={deal.id} className="flex justify-between items-center bg-white rounded-lg p-2.5 border">
                                <span className="text-sm font-medium text-gray-800">{deal.title}</span>
                                <div className="flex gap-2 items-center">
                                  <span className="text-xs text-gray-500">R$ {(deal.value || 0).toLocaleString('pt-BR')}</span>
                                  <Badge className={`text-xs ${deal.status === 'won' ? 'bg-green-100 text-green-700' : deal.status === 'lost' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{deal.status === 'won' ? 'Ganho' : deal.status === 'lost' ? 'Perdido' : 'Aberto'}</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Lead info extra */}
                      <div className="bg-slate-50 rounded-xl p-4">
                        <h4 className="font-semibold text-slate-700 text-sm mb-3">📋 Dados Adicionais</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          {selectedLead.company && <div><span className="font-medium">Empresa:</span> {selectedLead.company}</div>}
                          {selectedLead.segment && <div><span className="font-medium">Segmento:</span> {selectedLead.segment}</div>}
                          {selectedLead.source && <div><span className="font-medium">Origem:</span> {selectedLead.source}</div>}
                          {selectedLead.created_at && <div><span className="font-medium">Cadastrado:</span> {new Date(selectedLead.created_at).toLocaleDateString('pt-BR')}</div>}
                          {interactions.length > 0 && <div><span className="font-medium">Último contato:</span> {new Date(interactions[0].created_at).toLocaleDateString('pt-BR')}</div>}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {detailTab === 'interacoes' && <>
              {/* Add Interaction */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 mb-3">Nova Interação</h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="interaction-type">Tipo</Label>
                      <Select 
                        value={newInteraction.type} 
                        onValueChange={(value) => setNewInteraction({...newInteraction, type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="call">Ligação</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="meeting">Reunião</SelectItem>
                          <SelectItem value="note">Anotação</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="interaction-subject">Assunto</Label>
                      <Input
                        id="interaction-subject"
                        value={newInteraction.subject}
                        onChange={(e) => setNewInteraction({...newInteraction, subject: e.target.value})}
                        placeholder="Assunto da interação"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="interaction-description">Descrição</Label>
                    <Textarea
                      id="interaction-description"
                      value={newInteraction.description}
                      onChange={(e) => setNewInteraction({...newInteraction, description: e.target.value})}
                      placeholder="Descreva a interação..."
                      rows={3}
                    />
                  </div>
                  <Button onClick={handleAddInteraction} className="btn-primary">
                    Adicionar Interação
                  </Button>
                </div>
              </div>

              {/* Interactions History */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Histórico de Interações</h4>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {interactions.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Nenhuma interação registrada</p>
                  ) : (
                    interactions.map((interaction) => (
                      <div key={interaction.id} className="bg-white border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline">{interaction.type}</Badge>
                            <span className="font-medium text-sm">{interaction.subject}</span>
                          </div>
                          <span className="text-xs text-slate-500">
                            {new Date(interaction.created_at).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{interaction.description}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
              </>}

              {detailTab === 'negociacoes' && (
                <NegotiationsPanel entityType="lead" entityId={selectedLead.id} api={api} />
              )}

              {detailTab === 'documentos' && (
                <DocumentsPanel entityType="lead" entityId={selectedLead.id} api={api} />
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Análise de IA */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <span>🤖 Análise de IA</span>
              {aiAnalysis && (
                <Badge className={`ml-2 ${getScoreColor(aiAnalysis.score)}`}>
                  Score: {aiAnalysis.score}/100
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Insights gerados por inteligência artificial
            </DialogDescription>
          </DialogHeader>
          
          {aiAnalysis && (
            <div className="space-y-6">
              {/* Score e Probabilidade */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4">
                  <div className="text-center">
                    <div className={`text-2xl font-bold rounded-lg p-2 ${getScoreColor(aiAnalysis.score)}`}>
                      {aiAnalysis.score}/100
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Qualidade do Lead</p>
                    <p className="text-xs text-slate-500">{getScoreLabel(aiAnalysis.score)}</p>
                  </div>
                </Card>
                
                <Card className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {Math.round((aiAnalysis.conversion_probability || 0) * 100)}%
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Prob. Conversão</p>
                    <div className="flex items-center justify-center mt-1">
                      <span className="text-lg mr-1">{getSentimentEmoji(aiAnalysis.sentiment)}</span>
                      <p className="text-xs text-slate-500 capitalize">{aiAnalysis.sentiment}</p>
                    </div>
                  </div>
                </Card>
              </div>
              
              {/* Insights */}
              <div>
                <h4 className="font-medium text-slate-800 mb-2">💡 Insights da IA</h4>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                  <p className="text-sm text-slate-700">{aiAnalysis.insights}</p>
                </div>
              </div>
              
              {/* Próximas Ações */}
              <div>
                <h4 className="font-medium text-slate-800 mb-2">🎯 Próximas Ações Recomendadas</h4>
                <div className="space-y-2">
                  {aiAnalysis.next_actions.map((action, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 bg-green-50 rounded-lg">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-sm font-medium">
                        {index + 1}
                      </div>
                      <span className="text-sm text-slate-700">{action}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Resumo de Performance */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-800 mb-2">📊 Resumo da Análise</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-purple-600">{aiAnalysis.score}</div>
                    <div className="text-xs text-slate-600">Score IA</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-blue-600">
                      {Math.round((aiAnalysis.conversion_probability || 0) * 100)}%
                    </div>
                    <div className="text-xs text-slate-600">Conversão</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-green-600">{aiAnalysis.next_actions.length}</div>
                    <div className="text-xs text-slate-600">Ações</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
